import { Link, useParams } from "react-router-dom";
import SectionHeader from "../../../components/section/SectionHeader";
import SectionTitle from "../../../components/section/SectionTitle";
import StatusBadge from "../../../components/table/StatusBadge";
import PushFeedback from "../../../components/push-notification/PushFeedback";
import { usePushNotificationJob } from "../../../hooks/push-notifications";
import {
  formatPushDateTime,
  getPushStatusLabel,
  getPushStatusVariant,
} from "../../../helpers/push-notifications";

const deliveryMetrics = [
  ["matchingUsers", "Matching users"],
  ["totalRecipients", "Recipient installations"],
  ["acceptedCount", "Accepted by Firebase"],
  ["failedCount", "Failed"],
  ["filteredCount", "Filtered"],
];

function PushNotificationJobDetail() {
  const { jobId } = useParams();
  const jobQuery = usePushNotificationJob(jobId);
  const job = jobQuery.data;

  if (jobQuery.isLoading) {
    return <div className="rounded bg-white p-8 shadow">Loading notification job…</div>;
  }

  return (
    <div className="relative mb-6 flex w-full min-w-0 flex-col break-words rounded bg-white p-2 shadow-lg">
      <div className="rounded-t border-0 px-4 py-3 pb-6">
        <SectionHeader>
          <div>
            <SectionTitle title={`Push notification job #${jobId}`} />
            {job ? <h1 className="mt-2 text-2xl font-semibold">{job.title}</h1> : null}
          </div>
          <Link to="/push-notifications/history" className="text-sm font-semibold text-primary hover:underline">
            Back to history
          </Link>
        </SectionHeader>
      </div>
      <PushFeedback errors={jobQuery.error ? [jobQuery.error.message] : []} />
      {job ? (
        <div className="space-y-7 px-4 pb-8">
          <div className="flex flex-wrap items-center gap-4">
            <StatusBadge
              label={getPushStatusLabel(job.status, job.totalRecipients)}
              variant={getPushStatusVariant(job.status)}
            />
            {jobQuery.isFetching ? <span className="text-xs text-slate-500">Refreshing status…</span> : null}
          </div>
          <p className="whitespace-pre-wrap text-slate-700">{job.message}</p>
          {job.imageUrl ? (
            <img src={job.imageUrl} alt="" className="max-h-72 rounded object-contain" />
          ) : null}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {deliveryMetrics.map(([key, label]) => (
              <div key={key} className="rounded border border-slate-200 bg-slate-50 p-4">
                <div className="text-2xl font-semibold">{job[key] ?? 0}</div>
                <div className="mt-1 text-xs text-slate-500">{label}</div>
              </div>
            ))}
          </div>
          <dl className="grid gap-3 text-sm sm:grid-cols-[12rem_1fr]">
            <dt className="font-semibold">Category</dt><dd>{job.category}</dd>
            <dt className="font-semibold">Platforms</dt><dd>{job.platforms?.join(", ")}</dd>
            <dt className="font-semibold">Audience</dt><dd>{job.audienceType}</dd>
            <dt className="font-semibold">Created by</dt><dd>{job.createdBy}</dd>
            <dt className="font-semibold">Created</dt><dd>{formatPushDateTime(job.createdOn)}</dd>
            <dt className="font-semibold">Started</dt><dd>{formatPushDateTime(job.startedOn)}</dd>
            <dt className="font-semibold">Completed</dt><dd>{formatPushDateTime(job.completedOn)}</dd>
          </dl>
          {job.failureSummary ? (
            <div role="alert" className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {job.failureSummary}
            </div>
          ) : null}
          <p className="text-sm text-slate-500">
            Accepted means Firebase accepted the request. It does not guarantee delivery to or display on a device.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default PushNotificationJobDetail;
